import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { crmApi, formatAED } from "@/lib/crm";
import { useSettings, stageBadgeClass } from "@/contexts/SettingsContext";
import { Search, Plus } from "lucide-react";
import { format } from "date-fns";
import NewLeadDialog from "@/components/crm/NewLeadDialog";

export default function Leads() {
  const { settings } = useSettings();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [service, setService] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    crmApi.listLeads({ q: q || undefined, status: status || undefined, service: service || undefined })
      .then(setLeads)
      .finally(() => setLoading(false));
  };

  useEffect(load, [q, status, service]);

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] tracking-overline uppercase text-neutral-500">Database</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl">Leads</h1>
          <div className="mt-2 text-sm text-neutral-500">{leads.length} total</div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="leads-new-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase hover:opacity-90"
        >
          <Plus size={14}/> Add lead
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="leads-search"
            placeholder="Search name, email, phone, message…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 text-sm outline-none focus:border-neutral-900 dark:focus:border-white transition"
          />
        </div>
        <select data-testid="leads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2.5 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 text-sm outline-none">
          <option value="">All statuses</option>
          {(settings.stages || []).map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
        </select>
        <select data-testid="leads-service-filter" value={service} onChange={(e) => setService(e.target.value)} className="px-3 py-2.5 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 text-sm outline-none">
          <option value="">All services</option>
          {(settings.services || []).map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
        </select>
      </div>

      <div className="border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="text-[11px] tracking-overline uppercase text-neutral-500">
              <th className="text-left py-3 px-4 font-normal">Name</th>
              <th className="text-left py-3 px-4 font-normal">Service</th>
              <th className="text-left py-3 px-4 font-normal">Status</th>
              <th className="text-left py-3 px-4 font-normal">Source</th>
              <th className="text-left py-3 px-4 font-normal">Proposal</th>
              <th className="text-left py-3 px-4 font-normal">Received</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center text-xs tracking-overline uppercase text-neutral-500">Loading…</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-sm text-neutral-500">No leads yet.</td></tr>
            ) : (
              leads.map((l) => (
                <tr key={l.id} className="border-t border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="py-3 px-4">
                    <Link to={`/crm/leads/${l.id}`} className="font-medium hover:underline">{l.full_name}</Link>
                    <div className="text-xs text-neutral-500">{l.email}</div>
                  </td>
                  <td className="py-3 px-4">{l.service}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block text-[10px] tracking-overline uppercase px-2 py-1 border ${stageBadgeClass((settings.stages || []).find(s => s.label === l.status)?.color)}`}>{l.status || "New Enquiry"}</span>
                  </td>
                  <td className="py-3 px-4 text-neutral-500">{l.source || "—"}</td>
                  <td className="py-3 px-4 tabular-nums">{l.proposal_value ? formatAED(l.proposal_value) : "—"}</td>
                  <td className="py-3 px-4 text-neutral-500">{l.created_at ? format(new Date(l.created_at), "d MMM yyyy") : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <NewLeadDialog open={showNew} onClose={() => setShowNew(false)} onCreated={load} />
    </div>
  );
}
